import React, { useCallback, useMemo } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Paper,
  InputAdornment,
  useTheme,
  FilterOptionsState,
  Breadcrumbs,
} from "@mui/material";
import { HelenaLogo } from "shared/features/helena/components/icons";
import { useAtomValue } from "jotai";
import {
  centralFolderList,
  centralFolderTreeNodes,
  customerFolderList,
  customerFolderTreeNodes,
  folderMap,
} from "../store";
import { KbFileIcon, KbFolderIcon, SearchIcon, SearchInput } from "components";
import { useNavigate } from "react-router-dom";
import { ArrowRightIcon } from "@mui/x-date-pickers";
import { generateBreadcrumbs } from "../helpers";

const CustomPaper = (props: any) => {
  const theme = useTheme();
  return (
    <Paper
      {...props}
      sx={{
        mt: 1,
        border: `1px solid ${theme.palette.neutral["015"]}`,
        borderRadius: 2,
        padding: "12px",
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
        "& .MuiAutocomplete-listbox": {
          padding: 0,
        },
        "& .MuiAutocomplete-option": {
          padding: "12px 16px",
          borderBottom: `1px solid ${theme.palette.neutral["005"]}`,
          "&:last-child": {
            borderBottom: "none",
          },
          "&[aria-selected='true']": {
            backgroundColor: theme.palette.neutral["005"],
          },
          "&:hover": {
            backgroundColor: theme.palette.neutral["005"],
          },
        },
        "& .MuiAutocomplete-noOptions": {
          color: theme.palette.lightBg.low,
          ...theme.typography["body-medium"],
        },
      }}
    />
  );
};

export const KbSearch = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [inputValue, setInputValue] = React.useState<string>("");
  const [open, setOpen] = React.useState<boolean>(false);
  const folderMapValue = useAtomValue(folderMap);
  const centralKbFolderList = useAtomValue(centralFolderTreeNodes);
  const customerKbFolderList = useAtomValue(customerFolderTreeNodes);
  const kbFolderList = useMemo(() => {
    return [...centralKbFolderList, ...customerKbFolderList];
  }, [centralKbFolderList, customerKbFolderList]);

  const filterNodes = useCallback(
    (options: any[], state: FilterOptionsState<any>) => {
      const { inputValue } = state;
      if (!inputValue || inputValue.trim() === "" || inputValue.length < 2) {
        return options;
      }

      const searchTerm = inputValue.toLowerCase().trim();
      const r = options.filter((option) => {
        const matches = option.name?.toLowerCase().includes(searchTerm);
        console.log("Match result:", matches);
        return matches;
      });
      console.log("Filtered results:", r);
      return r;
    },
    [],
  );

  const folderList = useMemo(() => {
    const list = Array.from(folderMapValue.values());
    // Deduplicate by mapping_id
    const uniqueMap = new Map();
    list.forEach((item) => {
      uniqueMap.set(item.mapping_id, item);
    });
    return Array.from(uniqueMap.values());
  }, [folderMapValue]);

  return (
    <Box sx={{ width: "100%", mx: "auto", p: "32px" }}>
      {/* <pre>{JSON.stringify(folderList, null, 2)}</pre> */}
      <Autocomplete
        open={open}
        onClose={() => setOpen(false)}
        value={null}
        onChange={(event, newValue) => {
          if (newValue) {
            const route =
              newValue.kb_type === "folder"
                ? `/app/kb/folder/${newValue.mapping_id}`
                : `/app/kb/document/${newValue.article_id}`;
            navigate(route);
          }
          // setValue(newValue);
        }}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          if (newInputValue.length > 2) {
            setOpen(true);
          } else {
            setOpen(false);
          }
          setInputValue(newInputValue);
        }}
        options={folderList}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) =>
          option.mapping_id === value.mapping_id
        }
        filterOptions={filterNodes}
        noOptionsText="No articles or folders found"
        ListboxProps={{
          sx: {
            padding: "12px",
          },
        }}
        renderInput={(params) => (
          <SearchInput
            {...params}
            name="name"
            placeholder="Search existing articles and folders"
            // InputProps={{
            //   ...params.InputProps,
            //   startAdornment: (
            //     <InputAdornment position="start">
            //       <SearchIcon fontSize="small" />
            //     </InputAdornment>
            //   ),
            // }}
          />
        )}
        renderOption={(props, option) => {
          const breadcrumbPath = generateBreadcrumbs(
            option.mapping_id,
            folderMapValue,
          );

          return (
            <Box
              component="li"
              {...props}
              sx={{
                borderRadius: "8px",
                borderBottom: "none !important",
                "&:not(:last-child)": {
                  borderBottom: "none !important",
                },
              }}
              key={option.mapping_id}
              onClick={(event) => {
                if (event.metaKey || event.altKey) {
                  event.preventDefault();
                  event.stopPropagation();
                  const route =
                    option.kb_type === "folder"
                      ? `/app/kb/folder/${option.mapping_id}`
                      : `/app/kb/file/${option.mapping_id}`;
                  navigate(route);
                } else {
                  // Call the original onClick if it exists
                  if (props.onClick) {
                    props.onClick(event);
                  }
                }
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  width: "100%",
                  gap: 1,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body-small"
                    sx={{
                      color: theme.palette.lightBg.high,
                    }}
                  >
                    {option.name}
                  </Typography>
                  <Breadcrumbs
                    separator={
                      <Typography
                        variant="body-small"
                        color={theme.palette.lightBg.low}
                      >
                        /
                      </Typography>
                    }
                    sx={{
                      mt: 0.25,
                      "& .MuiBreadcrumbs-separator": {
                        // mx: "4px",
                        color: theme.palette.lightBg.low,
                      },
                      "& .MuiBreadcrumbs-li": {
                        ...theme.typography["body-xsmall"],
                      },
                    }}
                  >
                    {breadcrumbPath.map((item, index) => {
                      const [name, data] = Object.entries(item)[0];

                      return (
                        <Box
                          component="span"
                          key={typeof data === "object" ? data.id : data}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            ...theme.typography["body-xsmall"],
                            color: theme.palette.lightBg.low,
                          }}
                        >
                          <KbFolderIcon style={{ fontSize: "16px" }} />
                          {name}
                        </Box>
                      );
                    })}
                  </Breadcrumbs>
                </Box>
              </Box>
            </Box>
          );
        }}
        PaperComponent={CustomPaper}
        // freeSolo
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        sx={{
          height: "32px",
          "& .MuiAutocomplete-inputRoot": {
            height: "32px",
            minHeight: "32px",
            padding: "0 !important",
            paddingX: "16px !important",
          },
          "& .MuiAutocomplete-input": {
            padding: "0 !important",
          },
          "& .MuiAutocomplete-endAdornment": {
            top: "calc(50% - 12px)",
          },
        }}
      />
    </Box>
  );
};
